import React, { useState } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import JSZip from "jszip"; // For bundling images into a .zip
import { saveAs } from "file-saver"; // For saving the .zip

/*
 * FormatConverter (with Rotate & Grayscale)
 *
 * Features:
 *   • Converts images between JPEG, PNG, and WebP in-browser using <canvas>.
 *   • Optionally adjusts quality for JPEG/WebP.
 *   • Supports a naive SVG placeholder conversion.
 *   • Adds simple transformations: rotate in 90° increments and grayscale.
 *   • Reset functionality.
 *   • Light inline feedback on status & counts.
 */

const FormatConverter = () => {
  /* -------------------------------
   * 1) States
   * ------------------------------- */
  const [images, setImages] = useState([]);
  const [convertedImages, setConvertedImages] = useState([]);

  // Format selection + quality
  const [targetFormat, setTargetFormat] = useState("image/jpeg");
  const [quality, setQuality] = useState(0.8);

  // Transformation settings
  const [rotate, setRotate] = useState(0); // 0, 90, 180, 270
  const [grayscale, setGrayscale] = useState(false);

  /* -------------------------------
   * 2) Handle File Upload
   * ------------------------------- */
  const handleFileChange = (e) => {
    const { files } = e.target;
    if (!files || files.length === 0) return;

    const fileArray = [];
    for (let i = 0; i < files.length; i++) {
      fileArray.push(files[i]);
    }

    // Convert each selected file to base64 or text
    Promise.all(
      fileArray.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();

            // If it's an SVG, read as text
            if (file.type === "image/svg+xml") {
              reader.onload = (event) =>
                resolve({
                  name: file.name,
                  content: event.target.result,
                  isSvg: true,
                });
              reader.onerror = reject;
              reader.readAsText(file);
            } else {
              // Otherwise, read as dataURL
              reader.onload = (event) =>
                resolve({
                  name: file.name,
                  content: event.target.result,
                  isSvg: false,
                });
              reader.onerror = reject;
              reader.readAsDataURL(file);
            }
          })
      )
    )
      .then((items) => {
        setImages(items);
        setConvertedImages([]);
      })
      .catch((err) => console.error("File read error:", err));
  };

  /* -------------------------------
   * 3) Conversion + Transform Logic
   * ------------------------------- */
  const handleConvert = async () => {
    if (!images.length) return;

    const results = [];

    for (let i = 0; i < images.length; i++) {
      const { name, content, isSvg } = images[i];

      // SVG => naive approach => load <img>, draw to canvas
      if (isSvg) {
        if (targetFormat === "image/svg+xml") {
          // Keep as SVG
          results.push({
            name: changeExtension(name, "svg"),
            dataUrl: `data:image/svg+xml;base64,${btoa(content)}`,
          });
        } else {
          // Convert the SVG text to a dataURL, then rasterize + transform
          const blob = new Blob([content], { type: "image/svg+xml" });
          const url = URL.createObjectURL(blob);
          const rasterDataUrl = await rasterizeImage(
            url,
            targetFormat,
            quality,
            rotate,
            grayscale
          );
          results.push({
            name: changeExtension(name, mimeToExtension(targetFormat)),
            dataUrl: rasterDataUrl,
          });
          URL.revokeObjectURL(url);
        }
      } else {
        // For non-SVG images => draw onto a canvas + transforms
        const convertedDataUrl = await rasterizeImage(
          content,
          targetFormat,
          quality,
          rotate,
          grayscale
        );
        results.push({
          name: changeExtension(name, mimeToExtension(targetFormat)),
          dataUrl: convertedDataUrl,
        });
      }
    }

    setConvertedImages(results);
  };

  // Helper => draw image on canvas, apply rotate & grayscale, then export dataURL
  const rasterizeImage = (src, mime, qualityVal, rotateDeg, gray) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous"; // attempt to avoid CORS issues
      img.onload = () => {
        // Determine new bounding box for rotated images
        let finalWidth = img.width;
        let finalHeight = img.height;
        if (rotateDeg === 90 || rotateDeg === 270) {
          finalWidth = img.height;
          finalHeight = img.width;
        }

        const canvas = document.createElement("canvas");
        canvas.width = finalWidth;
        canvas.height = finalHeight;
        const ctx = canvas.getContext("2d");

        // Apply grayscale filter if specified
        if (gray) {
          ctx.filter = "grayscale(100%)";
        }

        // Move the origin to the center for rotation
        ctx.translate(finalWidth / 2, finalHeight / 2);
        // Rotate in radians
        const angleInRadians = (rotateDeg * Math.PI) / 180;
        ctx.rotate(angleInRadians);

        // Draw image offset so it’s centered after rotation
        if (rotateDeg === 90 || rotateDeg === 270) {
          ctx.drawImage(img, -img.width / 2, -img.height / 2);
        } else {
          ctx.drawImage(img, -finalWidth / 2, -finalHeight / 2);
        }

        const converted = canvas.toDataURL(mime, qualityVal);
        resolve(converted);
      };
      img.onerror = reject;
      img.src = src;
    });
  };

  // Helper => change file extension
  const changeExtension = (filename, newExt) => {
    return filename.replace(/\.[^/.]+$/, "") + "." + newExt;
  };

  // Convert MIME type to typical extension
  const mimeToExtension = (mime) => {
    switch (mime) {
      case "image/jpeg":
        return "jpg";
      case "image/png":
        return "png";
      case "image/webp":
        return "webp";
      case "image/svg+xml":
        return "svg";
      default:
        return "img";
    }
  };

  /* -------------------------------
   * 4) Download Logic
   * ------------------------------- */
  const downloadSingle = (item) => {
    const link = document.createElement("a");
    link.href = item.dataUrl;
    link.download = item.name;
    link.click();
  };

  const downloadAllAsZip = async () => {
    if (!convertedImages.length) return;
    const zip = new JSZip();

    for (let i = 0; i < convertedImages.length; i++) {
      const { name, dataUrl } = convertedImages[i];
      const base64Content = dataUrl.split(",")[1];
      zip.file(name, base64Content, { base64: true });
    }

    const blob = await zip.generateAsync({ type: "blob" });
    saveAs(blob, "converted_images.zip");
  };

  /* -------------------------------
   * 5) Reset Logic
   * ------------------------------- */
  const handleReset = () => {
    setImages([]);
    setConvertedImages([]);
    setTargetFormat("image/jpeg");
    setQuality(0.8);
    setRotate(0);
    setGrayscale(false);
  };

  /* -------------------------------
   * 6) Render
   * ------------------------------- */
  const numUploaded = images.length;
  const numConverted = convertedImages.length;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          mb: 3,
          py: 2,
          textAlign: "center",
          backgroundColor: "#f2f2f2",
        }}
      >
        <Typography variant="h4" gutterBottom align="center" sx={{ mb: 1 }}>
          Format Converter
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Convert and optionally transform (rotate, grayscale) images
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        {/* File Upload Panel */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Button variant="outlined" component="label" sx={{ borderRadius: 2 }}>
            Upload Images
            <input
              type="file"
              accept="image/*, .svg"
              multiple
              hidden
              onChange={handleFileChange}
            />
          </Button>
          <Typography variant="body2" color="text.secondary" display="inline">
            {numUploaded ? `${numUploaded} file(s) selected` : "No images yet"}
          </Typography>
        </Box>

        {/* Thumbnails of original files */}
        {numUploaded > 0 && (
          <Box
            sx={{
              mb: 3,
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {images.map((item, i) => (
              <Box
                key={i}
                component="img"
                src={
                  item.isSvg
                    ? `data:image/svg+xml;base64,${btoa(item.content)}`
                    : item.content
                }
                alt={"input-" + i}
                sx={{
                  width: 50,
                  height: 50,
                  objectFit: "cover",
                  borderRadius: 1,
                  border: "1px solid #ccc",
                }}
              />
            ))}
          </Box>
        )}

        {/* Format & Quality Selection */}
        {numUploaded > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Format</InputLabel>
              <Select
                label="Format"
                value={targetFormat}
                onChange={(e) => setTargetFormat(e.target.value)}
              >
                <MenuItem value="image/jpeg">JPEG</MenuItem>
                <MenuItem value="image/png">PNG</MenuItem>
                <MenuItem value="image/webp">WebP</MenuItem>
                <MenuItem value="image/svg+xml">SVG*</MenuItem>
              </Select>
            </FormControl>

            {/* Only show quality if format is JPEG or WebP */}
            {(targetFormat === "image/jpeg" ||
              targetFormat === "image/webp") && (
              <Box sx={{ width: 200 }}>
                <Typography variant="body2" gutterBottom>
                  Quality: {quality.toFixed(2)}
                </Typography>
                <Slider
                  value={quality}
                  min={0.0}
                  max={1.0}
                  step={0.01}
                  onChange={(e, val) => setQuality(val)}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Transform Controls */}
        {numUploaded > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 3 }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Rotate</InputLabel>
              <Select
                label="Rotate"
                value={rotate}
                onChange={(e) => setRotate(Number(e.target.value))}
              >
                <MenuItem value={0}>0°</MenuItem>
                <MenuItem value={90}>90°</MenuItem>
                <MenuItem value={180}>180°</MenuItem>
                <MenuItem value={270}>270°</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={grayscale}
                  onChange={(e) => setGrayscale(e.target.checked)}
                />
              }
              label="Grayscale"
            />
          </Box>
        )}

        {/* Disclaimer */}
        {numUploaded > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            * Converting raster images to SVG won’t create true vector paths.
            Rotation & grayscale are approximate transformations that may reduce
            image quality.
          </Typography>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            sx={{ borderRadius: 2 }}
            disabled={!images.length}
            onClick={handleConvert}
          >
            Convert
          </Button>

          {convertedImages.length > 0 && (
            <Button
              variant="contained"
              color="success"
              sx={{ borderRadius: 2 }}
              onClick={downloadAllAsZip}
            >
              Download All (.zip)
            </Button>
          )}

          {numUploaded > 0 && (
            <Button
              variant="outlined"
              color="error"
              sx={{ borderRadius: 2 }}
              onClick={handleReset}
            >
              Reset
            </Button>
          )}
        </Box>

        {/* Converted Thumbnails */}
        {numConverted > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Converted Images ({numConverted})
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {convertedImages.map((item, idx) => (
                <Box key={idx} sx={{ textAlign: "center" }}>
                  <Box
                    component="img"
                    src={item.dataUrl}
                    alt={item.name}
                    sx={{
                      width: 70,
                      height: 70,
                      objectFit: "cover",
                      borderRadius: 1,
                      border: "1px solid #ccc",
                      mb: 1,
                    }}
                  />
                  <Button
                    variant="outlined"
                    sx={{ borderRadius: 2 }}
                    onClick={() => downloadSingle(item)}
                  >
                    {item.name}
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default FormatConverter;
